// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Third party
import { take, takeUntil } from 'rxjs';

// Local library
import {
  AccountFacade,
  ILanguageOption,
  LANGUAGE_PREFERENCE_FIELD,
  LanguageService
} from '@amwager/account/data-access';
import { SessionService } from '@amwager/data-access';
import {
  MatNotificationService,
  TranslatePipe,
  TranslationService,
  UnsubscribeObservableService
} from '@amwager/util';
import { Web } from '@amwager/util-ts';

// Matches the legacy cookie lifetime in frontend/js/preferences.js
const LANGUAGE_COOKIE_DAYS = 1;

/**
 * Language picker — switches the active language pack and persists the choice
 */
@Component({
  selector: 'feature-mat-language',
  templateUrl: './language.component.html',
  styleUrls: ['./language.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslatePipe
  ]
})
export class LanguageComponent
  extends UnsubscribeObservableService
  implements OnInit {

  readonly languages: ILanguageOption[] = this.languageService.languages;

  selectedLanguage = '';

  // Guards double-taps while the pack loads and the save round-trips
  isDisabled = false;

  // Code to restore when the save is rejected
  private previousLanguage = '';

  // Code awaiting a save response, '' when nothing is in flight
  private pendingLanguage = '';

  constructor(
    private readonly accountFacade: AccountFacade,
    private readonly languageService: LanguageService,
    private readonly translationService: TranslationService,
    private readonly sessionService: SessionService,
    private readonly matNotificationService: MatNotificationService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.selectedLanguage = this.languageService.resolve(
      this.translationService.language,
      this.sessionService.getLanguage()
    );

    this.accountFacade.getAccountPreferencesAPI();

    this.accountFacade.isGetAccountPreferencesSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback?.isSuccess === undefined) return;

        const preferences = callback?.response?.AccountPreferences;
        if (!preferences) return;

        // The account is the source of truth, so adopt its language on load
        const language = this.languageService.resolve(
          preferences.Language,
          this.sessionService.getLanguage()
        );

        if (language !== this.selectedLanguage) this.activate(language);
        else this.rememberLanguage(language);

        this.pendingLanguage = '';
        this.isDisabled = false;
        this.cdRef.markForCheck();
      });

    this.accountFacade.isUpsertAccountPrefenceFieldSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        // The upsert state is shared by every preference, so only react while
        // one of our own saves is in flight.
        if (!this.pendingLanguage || callback?.isSuccess !== false) return;

        this.pendingLanguage = '';
        this.activate(this.previousLanguage);
        this.showAlert('preference.language.change.error');
      });
  }

  /**
   * Select a language: load its pack, then persist the choice
   * @param {string} code Language code (e.g. 'es')
   * @returns {void}
   */
  setLanguage(code: string): void {
    if (this.isDisabled || code === this.selectedLanguage) return;

    this.previousLanguage = this.selectedLanguage;
    this.isDisabled = true;
    this.cdRef.markForCheck();

    // Legacy loads the pack first and only saves once it resolves, so a
    // missing pack never leaves the account pointing at unusable copy.
    this.translationService.use(code)
      .pipe(take(1), takeUntil(this.toUnsubscribe$))
      .subscribe((loaded) => {
        if (!loaded) {
          this.isDisabled = false;
          this.cdRef.markForCheck();
          this.showAlert('preference.language.change.error');
          return;
        }

        this.selectedLanguage = code;
        this.rememberLanguage(code);

        // Guests have no account preferences — the cookie alone drives their UI
        if (!this.sessionService.hasToken()) {
          this.isDisabled = false;
          this.cdRef.markForCheck();
          return;
        }

        this.pendingLanguage = code;
        this.accountFacade.upsertAccountPrefenceFieldAPI(LANGUAGE_PREFERENCE_FIELD, code);
      });
  }

  /**
   * Switch to a language pack and show it as selected
   * @param {string} code Language code (e.g. 'es')
   * @returns {void}
   */
  private activate(code: string): void {
    this.selectedLanguage = code;
    this.rememberLanguage(code);

    this.translationService.use(code)
      .pipe(take(1), takeUntil(this.toUnsubscribe$))
      .subscribe(() => {
        this.isDisabled = false;
        this.cdRef.markForCheck();
      });
  }

  /**
   * Mirror the language into the cookie so it survives a reload
   * @param {string} code Language code (e.g. 'es')
   * @returns {void}
   */
  private rememberLanguage(code: string): void {
    this.sessionService.setLanguage(code, this.cookieOptions());
  }

  /**
   * Show a translated single-button alert
   * @param {string} key Language key of the message
   * @returns {void}
   */
  private showAlert(key: string): void {
    this.matNotificationService.showAlert(
      this.translationService.translate('preference.language.change', 'Change language'),
      this.translationService.translate(key, 'Please try again.'),
      '',
      '',
      this.translationService.translate('Close', 'Close')
    );
  }

  /**
   * Cookie options for the Language cookie
   * @returns {Web.ICookie} Options scoped to the app domain
   */
  private cookieOptions(): Web.ICookie {
    return this.sessionService.getOptions('/', LANGUAGE_COOKIE_DAYS);
  }
}
